/* Industries concept (templates/page.ac-ind-*): page behaviour.

   - Drawings: every [data-ac-ind-draw] starts as a poster image rendered
     from the same scene. On capable devices (WebGL2, no reduced motion, no
     save-data) three.js and assets/ac-ind-gl.js load after page load, the
     first interaction and an idle slot; each drawing then swaps to a live
     canvas while it is off screen, fills to its working level once when it
     scrolls into view, and can be dragged to turn. Rendering is on demand
     and stops when nothing moves. Any failure leaves the posters.
   - Flow sheet: the pipe fills with scroll and marks the steps it reaches.
   - NSN lookup, dilution calculator, copy buttons, inquiry prefill.
   - Dev theme only: keeps ?view= on links between concept pages.
   Everything is optional: the pages read and work without this file. */
(function () {
  "use strict";
  if (window.__acIndBooted) return;
  window.__acIndBooted = true;

  /* fallback only; the theme hosts the same file (see loadGL) */
  var THREE_URL = "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.min.js";
  var mql = function (q) {
    return window.matchMedia && window.matchMedia(q).matches;
  };
  var reduceMotion = mql("(prefers-reduced-motion: reduce)");
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var TEST = !!window.AC_IND_TEST_MODE;

  function ready(fn) {
    if (document.readyState !== "loading") fn();
    else document.addEventListener("DOMContentLoaded", fn);
  }

  /* Shopify's CDN minifies theme scripts with a CommonJS transform that
     turns a literal import() into require(); build it at runtime. */
  function loadModule(url) {
    return Promise.resolve().then(function () {
      return new Function("u", "return import(u)")(url);
    });
  }
  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = resolve;
      s.onerror = function () {
        reject(new Error("failed to load " + src));
      };
      document.head.appendChild(s);
    });
  }

  /* ------------------------------------------------------------ */
  /* Drawings                                                       */
  /* ------------------------------------------------------------ */

  function glAllowed() {
    if (window.AC_IND_NO_GL) return false;
    if (!TEST && (reduceMotion || saveData)) return false;
    if (!window.WebGL2RenderingContext) return false;
    return true;
  }

  var firstInteraction = new Promise(function (resolve) {
    if (TEST) return resolve();
    var evs = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"];
    function go() {
      evs.forEach(function (e) {
        window.removeEventListener(e, go, true);
      });
      resolve();
    }
    evs.forEach(function (e) {
      window.addEventListener(e, go, { capture: true, passive: true });
    });
  });
  var pageLoaded = new Promise(function (resolve) {
    if (document.readyState === "complete") resolve();
    else window.addEventListener("load", resolve, { once: true });
  });
  function idle() {
    return new Promise(function (resolve) {
      if (window.requestIdleCallback) window.requestIdleCallback(resolve, { timeout: 1500 });
      else setTimeout(resolve, 200);
    });
  }

  var glPromise = null;
  function loadGL(src) {
    if (!glPromise) {
      glPromise = Promise.all([pageLoaded, firstInteraction])
        .then(idle)
        .then(function () {
          /* three.js r160 is hosted in the theme (ac-ind-three.module.min.js);
             jsDelivr is the fallback if the theme copy fails to load */
          var own = (document.querySelector("[data-three-src]") || { getAttribute: function () {} }).getAttribute("data-three-src");
          var three = own
            ? loadModule(own).catch(function () {
                return loadModule(THREE_URL);
              })
            : loadModule(THREE_URL);
          return Promise.all([three, window.AC_IND_GL ? null : loadScript(src)]);
        })
        .then(function (r) {
          var THREE = r[0];
          var eng = window.AC_IND_GL.create(THREE, { test: TEST, onLost: dropGL });
          return eng;
        });
    }
    return glPromise;
  }

  var engine = null;
  var draws = [];
  var dead = false;
  var guard = null;

  function dropGL() {
    dead = true;
    draws.forEach(function (d) {
      d.el.classList.remove("is-live");
    });
    if (engine) engine.destroy();
    engine = null;
  }

  function paletteOf(el) {
    var cs = getComputedStyle(el);
    var get = function (n, f) {
      var v = cs.getPropertyValue(n).trim();
      return v || f;
    };
    var paper = get("--draw-paper", "#ffffff");
    var dark = /^#(1|0)/i.test(paper) || el.getAttribute("data-palette") === "dark";
    return {
      paper: paper,
      ink: get("--draw-ink", "#1b1450"),
      accent: get("--draw-accent", "#5f59f7"),
      dark: dark,
    };
  }

  function sizeOf(d) {
    var r = d.el.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    return { w: Math.max(1, Math.round(r.width)), h: Math.max(1, Math.round(r.height)), dpr: dpr };
  }

  function paint(d) {
    if (!engine || dead) return;
    var sz = sizeOf(d);
    if (sz.w < 8 || sz.h < 8) return;
    var cw = Math.round(sz.w * sz.dpr);
    var ch = Math.round(sz.h * sz.dpr);
    if (d.canvas.width !== cw || d.canvas.height !== ch) {
      d.canvas.width = cw;
      d.canvas.height = ch;
    }
    engine.setPalette(paletteOf(d.el));
    /* supersample on 1x screens; at 2x and 3x the device pixels are enough.
       Capped at about 4 Mpx of offscreen buffer. */
    var scale = sz.dpr >= 2 ? Math.min(sz.dpr, 3) : 2;
    var px = sz.w * sz.h * scale * scale;
    if (px > 4e6) scale = Math.max(1, Math.sqrt(4e6 / (sz.w * sz.h)));
    var t0 = performance.now();
    try {
      engine.render(d.drawing, d.canvas, { w: sz.w, h: sz.h, scale: scale, yaw: d.yaw, level: d.level });
    } catch (e) {
      if (window.AC_IND_DEBUG) console.error("ac-ind drawing failed", e);
      dropGL();
      return;
    }
    if (guard && !TEST) guard(performance.now() - t0);
  }

  var raf = 0;
  function tick(now) {
    raf = 0;
    if (dead) return;
    var more = false;
    draws.forEach(function (d) {
      if (!d.live) return;
      var dirty = d.dirty;
      if (d.anim) {
        var t = Math.min(1, (now - d.anim.t0) / d.anim.ms);
        var e = 1 - Math.pow(1 - t, 3);
        d.level = d.anim.from + (1 - d.anim.from) * e;
        d.yaw = d.anim.yaw0 * (1 - e);
        dirty = true;
        if (t >= 1) d.anim = null;
        else more = true;
      }
      if (dirty) {
        d.dirty = false;
        paint(d);
      }
    });
    if (more) schedule();
  }
  function schedule() {
    if (!raf) raf = requestAnimationFrame(tick);
  }

  function goLive(d) {
    if (d.live || dead || !engine) return;
    try {
      d.drawing = engine.drawing(d.type);
    } catch (e) {
      if (window.AC_IND_DEBUG) console.error(e);
      return;
    }
    if (d.drawing.def && d.drawing.def.fixed) d.static = true;
    d.el.classList.toggle("is-static", !!d.static);
    d.canvas = document.createElement("canvas");
    d.canvas.setAttribute("aria-hidden", "true");
    d.el.appendChild(d.canvas);
    /* if it is already in view, show the finished drawing (no visible
       draining); otherwise start empty and fill when it is reached */
    var r = d.el.getBoundingClientRect();
    var inView = r.top < window.innerHeight && r.bottom > 0;
    d.level = d.static || inView ? 1 : 0;
    d.filled = d.level === 1;
    d.yaw = 0;
    d.live = true;
    paint(d);
    d.el.classList.add("is-live");
    if (!d.static) bindDrag(d);
  }

  function fill(d) {
    if (!d.live || d.filled) return;
    d.filled = true;
    d.anim = { t0: performance.now(), ms: 1500, from: d.level, yaw0: -16 };
    schedule();
  }

  function bindDrag(d) {
    var startX = 0;
    var startY = 0;
    var startYaw = 0;
    var down = false;
    var active = false;
    /* a drag starts only on a clearly horizontal move, so a vertical swipe
       that begins on a drawing scrolls the page and leaves the fill alone */
    d.canvas.addEventListener("pointerdown", function (e) {
      if (e.pointerType === "mouse" && e.button !== 0) return;
      down = true;
      active = false;
      startX = e.clientX;
      startY = e.clientY;
      startYaw = d.yaw;
    });
    d.canvas.addEventListener("pointermove", function (e) {
      if (!down) return;
      if (!active) {
        var dx = Math.abs(e.clientX - startX);
        if (dx < 6 || dx < Math.abs(e.clientY - startY)) return;
        active = true;
        if (d.anim || !d.filled) {
          d.anim = null;
          d.filled = true;
          d.level = 1;
          startYaw = d.yaw = 0;
        }
        try {
          d.canvas.setPointerCapture(e.pointerId);
        } catch (err) {
          /* not capturable */
        }
      }
      var lim = (d.drawing.def && d.drawing.def.maxYaw) || 70;
      d.yaw = Math.max(-lim, Math.min(lim, startYaw + (e.clientX - startX) * 0.45));
      d.dirty = true;
      schedule();
    });
    var end = function () {
      down = false;
      active = false;
    };
    d.canvas.addEventListener("pointerup", end);
    d.canvas.addEventListener("pointercancel", end);
  }

  function initDrawings(root) {
    var els = Array.prototype.slice.call(root.querySelectorAll("[data-ac-ind-draw]"));
    if (!els.length || !glAllowed()) return;
    var src = els[0].getAttribute("data-gl-src");
    /* static drawings (index tiles, the container lineup) keep their
       poster: going live would redraw the same picture */
    var fresh = els
      .filter(function (el) {
        return !el.__acInd && !el.hasAttribute("data-static");
      })
      .map(function (el) {
        var d = {
          el: el,
          type: (window.innerWidth < 990 && el.getAttribute("data-type-sm")) || el.getAttribute("data-type"),
          static: el.hasAttribute("data-static") || reduceMotion,
          live: false,
          level: 1,
          yaw: 0,
        };
        el.__acInd = d;
        draws.push(d);
        return d;
      });
    if (!fresh.length) return;

    var near = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          near.unobserve(en.target);
          var d = en.target.__acInd;
          loadGL(src)
            .then(function (eng) {
              if (dead) return;
              if (!engine) {
                engine = eng;
                guard = window.AC_IND_GL.watchdog(14, function (ms) {
                  if (window.AC_IND_DEBUG) console.warn("ac-ind drawings too slow (" + ms.toFixed(1) + " ms); using posters");
                  dropGL();
                });
              }
              goLive(d);
              if (d.reachedEarly) fill(d);
            })
            .catch(function (e) {
              if (window.AC_IND_DEBUG) console.warn("ac-ind drawings unavailable", e);
            });
        });
      },
      { rootMargin: "400px 0px" },
    );
    var reach = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          reach.unobserve(en.target);
          var d = en.target.__acInd;
          if (d.live) fill(d);
          else d.reachedEarly = true;
        });
      },
      { rootMargin: "0px 0px -22% 0px", threshold: 0.35 },
    );
    fresh.forEach(function (d) {
      near.observe(d.el);
      if (!d.static) reach.observe(d.el);
    });

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function (entries) {
        entries.forEach(function (en) {
          var d = en.target.__acInd;
          if (d && d.live) {
            d.dirty = true;
            schedule();
          }
        });
      });
      fresh.forEach(function (d) {
        ro.observe(d.el);
      });
    }
  }

  /* Theme dark-mode toggle: repaint with the new palette. */
  new MutationObserver(function () {
    draws.forEach(function (d) {
      if (d.live) d.dirty = true;
    });
    schedule();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ["color-mode"] });

  /* ------------------------------------------------------------ */
  /* Flow sheet pipe                                                */
  /* ------------------------------------------------------------ */

  function initFlow(root) {
    var lists = Array.prototype.slice.call(root.querySelectorAll("[data-ac-ind-flow]"));
    lists.forEach(function (list) {
      if (list.__acIndFlow) return;
      list.__acIndFlow = true;
      var steps = Array.prototype.slice.call(list.querySelectorAll(".ac-ind-step"));
      if (reduceMotion) {
        var lastFig = list.querySelectorAll(".ac-ind-step__fig");
        lastFig = lastFig[lastFig.length - 1];
        var pp = list.querySelector(".ac-ind-flow__pipe");
        if (pp && lastFig) pp.style.bottom = Math.max(0, list.getBoundingClientRect().bottom - lastFig.getBoundingClientRect().bottom) + "px";
        list.style.setProperty("--flow", "1");
        steps.forEach(function (s) {
          s.classList.add("is-reached");
        });
        return;
      }
      var pending = false;
      var pipe = list.querySelector(".ac-ind-flow__pipe");
      var figs = list.querySelectorAll(".ac-ind-step__fig");
      function fit() {
        /* end the pipe at the middle of the last drawing, not below the
           last step's product list */
        var last = figs[figs.length - 1];
        if (!pipe || !last) return;
        var lr = list.getBoundingClientRect();
        var fr = last.getBoundingClientRect();
        pipe.style.bottom = Math.max(0, lr.bottom - (fr.top + fr.height / 2)) + "px";
      }
      var visible = true;
      function update() {
        pending = false;
        /* read every rect first, then write */
        var r = list.getBoundingClientRect();
        var line = window.innerHeight * 0.62;
        var tops = steps.map(function (s) {
          return s.getBoundingClientRect().top;
        });
        var p = Math.max(0, Math.min(1, (line - r.top) / Math.max(1, r.height)));
        list.style.setProperty("--flow", p.toFixed(4));
        steps.forEach(function (s, k) {
          s.classList.toggle("is-reached", tops[k] < line);
        });
      }
      if ("IntersectionObserver" in window) {
        new IntersectionObserver(function (en) {
          visible = en[0].isIntersecting;
          if (visible) onScroll();
        }).observe(list);
      }
      function onScroll() {
        if (!visible) return;
        if (!pending) {
          pending = true;
          requestAnimationFrame(update);
        }
      }
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", function () {
        fit();
        onScroll();
      }, { passive: true });
      fit();
      update();
    });
  }

  /* ------------------------------------------------------------ */
  /* Copy buttons                                                   */
  /* ------------------------------------------------------------ */

  var live = null;
  function announce(text) {
    if (!live) {
      live = document.createElement("div");
      live.setAttribute("role", "status");
      live.className = "ac-ind-visually-hidden";
      live.style.cssText = "position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)";
      document.body.appendChild(live);
    }
    live.textContent = "";
    setTimeout(function () {
      live.textContent = text;
    }, 50);
  }

  function initCopy(root) {
    Array.prototype.slice.call(root.querySelectorAll("[data-ac-ind-copy]")).forEach(function (btn) {
      if (btn.__acInd) return;
      btn.__acInd = true;
      btn.hidden = false;
      var label = btn.textContent;
      btn.addEventListener("click", function () {
        var v = btn.getAttribute("data-ac-ind-copy");
        var done = function () {
          var msg = btn.getAttribute("data-done") || "Copied";
          btn.classList.add("is-done");
          btn.textContent = msg;
          announce(msg + ": " + v);
          setTimeout(function () {
            btn.classList.remove("is-done");
            btn.textContent = label;
          }, 1800);
        };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(v).then(done, function () {});
        else {
          var ta = document.createElement("textarea");
          ta.value = v;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          try {
            document.execCommand("copy");
            done();
          } catch (e) {
            /* nothing to do */
          }
          ta.remove();
        }
      });
    });
  }

  /* ------------------------------------------------------------ */
  /* NSN lookup                                                     */
  /* ------------------------------------------------------------ */

  function initNsn(root) {
    Array.prototype.slice.call(root.querySelectorAll("[data-ac-ind-nsn]")).forEach(function (box) {
      if (box.__acInd) return;
      box.__acInd = true;
      var input = box.querySelector("input");
      var out = box.querySelector("[data-nsn-results]");
      var status = box.querySelector("[data-nsn-status]");
      var src = box.getAttribute("data-src");
      var rows = null;
      var loading = null;
      function load() {
        if (!loading)
          loading = fetch(src, { credentials: "same-origin" })
            .then(function (r) {
              if (!r.ok) throw new Error("NSN list " + r.status);
              return r.json();
            })
            .then(function (d) {
              rows = Array.isArray(d) ? d : [];
              Array.prototype.slice.call(root.querySelectorAll("[data-fsc-count]")).forEach(function (el) {
                var f = el.getAttribute("data-fsc-count");
                var n = rows.filter(function (r) {
                  return String(r.FSC) === f;
                }).length;
                if (n) el.textContent = el.getAttribute("data-template").replace("{n}", n);
              });
            })
            .catch(function () {
              rows = [];
              if (status) status.textContent = box.getAttribute("data-error");
            });
        return loading;
      }
      var esc = function (s) {
        return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
          return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
        });
      };
      var norm = function (s) {
        return String(s || "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
      };
      function render(q) {
        var nq = norm(q);
        if (!nq) {
          out.innerHTML = "";
          if (status) status.textContent = "";
          return;
        }
        var hits = rows.filter(function (r) {
          return norm(r.NSN).indexOf(nq) > -1 || norm(r["PRODUCT NAME"]).indexOf(nq) > -1;
        });
        if (status) status.textContent = (hits.length === 1 ? box.getAttribute("data-one") : box.getAttribute("data-many")).replace("{n}", hits.length);
        out.innerHTML = hits
          .slice(0, 12)
          .map(function (r) {
            var safe = /^\/(?!\/)|^https:\/\//.test(String(r.URL || ""));
            var link = r.URL && safe ? '<a class="ac-ind-link" href="' + esc(r.URL) + '">' + esc(box.getAttribute("data-view")) + "</a>" : '<a class="ac-ind-link" href="' + esc(box.getAttribute("data-quote")) + "?source=nsn&q=" + encodeURIComponent(r.NSN) + '">' + esc(box.getAttribute("data-ask")) + "</a>";
            return (
              '<li class="ac-ind-nsn__hit"><span class="ac-ind-mono ac-ind-nsn__num">' +
              esc(r.NSN) +
              '</span><span class="ac-ind-nsn__name">' +
              esc(r["PRODUCT NAME"]) +
              "</span><span class=\"ac-ind-nsn__unit\">" +
              esc(r["UNIT OF ISSUE"]) +
              "</span>" +
              link +
              "</li>"
            );
          })
          .join("");
      }
      var t;
      input.addEventListener("focus", load, { once: true });
      input.addEventListener("input", function () {
        clearTimeout(t);
        var v = input.value;
        t = setTimeout(function () {
          load().then(function () {
            render(v);
          });
        }, 120);
      });
      /* show counts on the FSC tiles without waiting for a search */
      if ("IntersectionObserver" in window) {
        var io = new IntersectionObserver(function (en) {
          if (en[0].isIntersecting) {
            io.disconnect();
            load();
          }
        });
        io.observe(box);
      }
    });
  }

  /* ------------------------------------------------------------ */
  /* Dilution calculator (C1 V1 = C2 V2)                            */
  /* ------------------------------------------------------------ */

  function initCalc(root) {
    Array.prototype.slice.call(root.querySelectorAll("[data-ac-ind-calc]")).forEach(function (form) {
      if (form.__acInd) return;
      form.__acInd = true;
      var f = function (n) {
        return form.querySelector('[name="' + n + '"]');
      };
      var out = form.querySelector("[data-calc-out]");
      var bar = form.querySelector("[data-calc-bar]");
      function num(el) {
        var v = parseFloat(String(el.value).replace(",", "."));
        return isFinite(v) ? v : NaN;
      }
      function update() {
        var c1 = num(f("c1"));
        var c2 = num(f("c2"));
        var v2 = num(f("v2"));
        var unit = f("unit").value;
        var msg;
        if (bar) bar.style.removeProperty("--share");
        if (!(c1 > 0) || !(c2 > 0) || !(v2 > 0)) msg = form.getAttribute("data-empty");
        else if (c2 >= c1) msg = form.getAttribute("data-too-strong");
        else {
          var v1 = (c2 * v2) / c1;
          var water = v2 - v1;
          var fmt = function (x) {
            return x >= 100 ? x.toFixed(0) : x >= 10 ? x.toFixed(1) : x >= 0.01 ? x.toFixed(2) : String(Number(x.toPrecision(2)));
          };
          msg = form
            .getAttribute("data-result")
            .replace("{v1}", fmt(v1))
            .replace("{water}", fmt(water))
            .replace(/\{unit\}/g, unit)
            .replace("{v2}", fmt(v2))
            .replace("{c2}", String(c2));
          if (bar) bar.style.setProperty("--share", Math.max(0.02, Math.min(1, v1 / v2)).toFixed(4));
        }
        out.textContent = msg;
      }
      form.addEventListener("input", update);
      form.addEventListener("submit", function (e) {
        e.preventDefault();
        update();
      });
      update();
    });
  }

  /* ------------------------------------------------------------ */
  /* Contact form: ?inquiry= and ?industry= from older links        */
  /* ------------------------------------------------------------ */

  function initInquiry(root) {
    var form = root.querySelector("[data-ac-ind-inquiry]");
    if (!form || form.__acInd) return;
    form.__acInd = true;
    var p = new URLSearchParams(location.search);
    var sel = form.querySelector('[name="contact[Topic]"]');
    var inq = (p.get("inquiry") || "").toLowerCase();
    if (sel && inq) {
      Array.prototype.slice.call(sel.options).forEach(function (o) {
        if (o.value.toLowerCase().indexOf(inq) === 0 || o.getAttribute("data-key") === inq) sel.value = o.value;
      });
    }
    var ind = p.get("industry");
    var body = form.querySelector('[name="contact[body]"]');
    if (ind && body && !body.value) body.value = form.getAttribute("data-industry-line").replace("{industry}", ind.replace(/[-_]+/g, " ")) + "\n\n";
  }

  /* ------------------------------------------------------------ */
  /* Analytics: product and quote clicks (through acGA4, which      */
  /* checks consent and hands the event to the next page)           */
  /* ------------------------------------------------------------ */

  function track(name, params) {
    var ga = window.acGA4;
    if (!ga || typeof ga.trackNavigationEvent !== "function") return;
    try {
      ga.trackNavigationEvent(name, params);
    } catch (e) {
      /* analytics must never block the click */
    }
  }
  document.addEventListener("click", function (e) {
    var a = e.target.closest && e.target.closest(".ac-ind a[href]");
    if (!a) return;
    var page = location.pathname.replace(/\/$/, "");
    var h1 = document.querySelector(".ac-ind-hero h1");
    var listName = h1 ? h1.textContent.trim() : document.title;
    if (a.hasAttribute("data-ac-ind-item")) {
      track("select_item", {
        item_list_id: page,
        item_list_name: listName,
        items: [
          {
            item_id: a.getAttribute("data-ac-ind-item"),
            item_name: a.getAttribute("data-ac-ind-item-name"),
            index: Number(a.getAttribute("data-ac-ind-index")) || 0,
            item_category: a.getAttribute("data-ac-ind-step-name") || "",
          },
        ],
        process_step: Number(a.getAttribute("data-ac-ind-step")) || 0,
      });
    } else if (/\/pages\/request-a-quote/.test(a.getAttribute("href"))) {
      track("quote_open", { source: "industries_concept", page: page });
    }
  });

  /* ------------------------------------------------------------ */
  /* Dev theme only: keep ?view= between concept pages              */
  /* ------------------------------------------------------------ */

  var VIEWS = {
    "/pages/industries": "ac-ind-hub",
    "/pages/for-businesses": "ac-ind-hub",
    "/pages/chemicals-for-water-treatment": "ac-ind-water",
    "/pages/chemicals-for-semiconductor-manufacturing": "ac-ind-semiconductor",
    "/pages/chemicals-for-aerospace-defense": "ac-ind-aerospace",
    "/pages/chemicals-for-craft-distilling": "ac-ind-distilling",
    "/pages/chemicals-for-data-center-cooling": "ac-ind-datacenter",
    "/pages/chemicals-for-extraction-processing": "ac-ind-extraction",
    "/pages/chemicals-for-food-processing": "ac-ind-food",
    "/pages/chemicals-for-government-municipal": "ac-ind-municipal",
    "/pages/chemicals-for-hvac-geothermal": "ac-ind-hvac",
    "/pages/chemicals-for-laboratory-research": "ac-ind-lab",
    "/pages/chemicals-for-metal-finishing": "ac-ind-metal",
    "/pages/chemicals-for-oil-gas": "ac-ind-oilgas",
    "/pages/chemicals-for-paper-pulp": "ac-ind-pulp",
    "/pages/chemicals-for-cosmetics-personal-care": "ac-ind-cosmetics",
    "/pages/nsn": "ac-ind-gov",
    "/pages/request-a-quote": "ac-ind-quote",
    "/pages/quick-order": "ac-ind-quick",
    "/pages/credit-application": "ac-ind-credit",
    "/pages/tax-exempt": "ac-ind-tax",
    "/pages/subscribe-and-save": "ac-ind-subscribe",
    "/pages/subscribe-save": "ac-ind-subscribe",
    "/pages/faq": "ac-ind-faq",
    "/pages/shipping-guide": "ac-ind-shipping",
    "/pages/safety-data-sheets": "ac-ind-sds",
    "/pages/coa-request": "ac-ind-coa",
    "/pages/about-us": "ac-ind-about",
    "/pages/contact": "ac-ind-contact",
    "/pages/certifications": "ac-ind-certs",
    "/pages/press": "ac-ind-press",
    "/pages/services": "ac-ind-services",
    "/pages/resources": "ac-ind-res",
  };
  function keepViews() {
    var th = window.Shopify && window.Shopify.theme;
    if (!th || th.role === "main") return;
    if (!/[?&]view=ac-ind-/.test(location.search)) return;
    Array.prototype.slice.call(document.querySelectorAll("a[href]")).forEach(function (a) {
      var u;
      try {
        u = new URL(a.getAttribute("href"), location.href);
      } catch (e) {
        return;
      }
      if (u.host !== location.host && !/alliancechemical\.com$/.test(u.host)) return;
      var path = u.pathname.replace(/\/$/, "");
      var view = VIEWS[path];
      if (!view || u.searchParams.has("view")) return;
      u.host = location.host;
      u.protocol = location.protocol;
      if (path === "/pages/resources") u.pathname = "/pages/faq";
      u.searchParams.set("view", view);
      a.setAttribute("href", u.pathname + u.search + u.hash);
    });
  }

  /* ------------------------------------------------------------ */
  /* Boot and Theme Editor lifecycle                                */
  /* ------------------------------------------------------------ */

  function init(root) {
    root = root || document;
    initDrawings(root);
    initFlow(root);
    initCopy(root);
    initNsn(root);
    initCalc(root);
    initInquiry(root);
  }

  ready(function () {
    init(document);
    keepViews();
  });
  document.addEventListener("shopify:section:load", function (e) {
    init(e.target);
  });
  document.addEventListener("shopify:section:unload", function (e) {
    draws = draws.filter(function (d) {
      if (!e.target.contains(d.el)) return true;
      if (engine && d.drawing) engine.dispose(d.drawing);
      return false;
    });
  });
})();
